'use client';

import { Link } from '@/i18n/navigation';
import { type ReactNode, useEffect, useId, useState } from 'react';
import SvgChevronDown from './svg/SvgChevronDown';

type SubmenuItem = {
    href: string;
    label: string;
};

type BurgerSubmenuProps = {
    items: SubmenuItem[];
    children: ReactNode;
    activeNav?: boolean;
    onClick?: () => void;
};

export default function BurgerSubmenu({ items, children, activeNav, onClick }: BurgerSubmenuProps) {
    const [active, setActive] = useState(false);
    const panelId = useId();

    const isOpen = active && activeNav !== false;

    // Collapse the submenu whenever the burger menu closes, so navigating away
    // and reopening the menu doesn't reveal a previously-expanded submenu.
    useEffect(() => {
        if (activeNav === false) setActive(false);
    }, [activeNav]);

    return (
        <div className="flex flex-col">
            <button
                type="button"
                className="relative inline-flex items-center justify-center gap-2 leading-none p-6 font-medium cursor-pointer"
                onClick={() => setActive(!active)}
                aria-expanded={isOpen}
                aria-controls={panelId}
            >
                {children}
                <span
                    className={`relative top-0.5 transform transition-transform duration-300 ${
                        isOpen ? 'rotate-180' : ''
                    }`}
                >
                    <SvgChevronDown width={10} height={6} />
                </span>
            </button>

            <div id={panelId} className={`accordion w-full ${isOpen ? 'accordion-open' : ''}`}>
                <div className="flex w-full min-w-max bg-white flex-col rounded-[15px] px-7 shadow-submenu">
                    {items.map(({ href, label }) => (
                        <Link
                            href={href}
                            key={href}
                            className="text-black flex py-6 border-b last:border-0 justify-center items-center"
                            onClick={onClick}
                        >
                            {label}
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
}
